import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import {
  Factory, FlaskConical, ShoppingCart, FileText, Truck, Wallet, History, Download, Save, Plus, X, Filter, Sparkles, Bookmark,
  ArrowDownWideNarrow, Rows3, Columns3, Sigma, CalendarRange, BarChart3,
} from 'lucide-react';
import { reportAPI } from '../utils/api';
import SectionHero from '../components/common/SectionHero';
import Modal from '../components/common/Modal';
import { fmt, apiError, downloadBlob, today } from '../utils/helpers';

const DS_ICON = { production: Factory, material_use: FlaskConical, sales: ShoppingCart, lading: FileText, purchases: Truck, expenses: Wallet, stock_moves: History };
const DS_COLOR = { production: '#818cf8', material_use: '#fbbf24', sales: '#34d399', lading: '#38bdf8', purchases: '#2dd4bf', expenses: '#fb923c', stock_moves: '#a78bfa' };
const RANGES = ['this_month', 'last_month', 'this_quarter', 'this_year', 'jthis_year', 'last_6_months', 'last_12_months', 'last_year', 'all', 'custom'];
const PALETTE = ['#818cf8', '#34d399', '#fbbf24', '#f472b6', '#38bdf8', '#fb923c', '#a78bfa', '#2dd4bf', '#f87171', '#94a3b8', '#facc15', '#4ade80'];
const INT_MEASURES = ['batches', 'sales', 'bills', 'purchases', 'count'];
const numFmt = (m, v) => (v === null || v === undefined ? '' : fmt(v, INT_MEASURES.includes(m) ? 0 : m === 'weight_t' ? 3 : 2));

export default function ReportBuilder() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [meta, setMeta] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState(null);     // {key, dir} client side
  const [filterPick, setFilterPick] = useState(null);
  const [saveBox, setSaveBox] = useState(null);
  const [title, setTitle] = useState('');
  const reqId = useRef(0);

  const loadMeta = () => reportAPI.builderMeta(lang).then(r => {
    setMeta(r.data);
    setCfg(c => {
      if (c) return c;
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem('rb_cfg') || 'null'); } catch (e) { /* ignore */ }
      if (saved && r.data.datasets.some(d => d.key === saved.dataset)) return saved;
      const first = r.data.builtin[0];
      if (first) setTitle(first.name);
      return first ? { ...first.config } : { dataset: r.data.datasets[0].key, range: 'this_month', ...r.data.datasets[0].default };
    });
  }).catch(() => toast.error(t('error')));
  useEffect(() => { loadMeta(); }, [lang]); // eslint-disable-line

  useEffect(() => {
    if (!cfg || !meta) return;
    if (cfg.range === 'custom' && (!cfg.date_from || !cfg.date_to)) return;
    try { localStorage.setItem('rb_cfg', JSON.stringify(cfg)); } catch (e) { /* ignore */ }
    const id = ++reqId.current;
    setBusy(true);
    const h = setTimeout(() => reportAPI.builderRun(cfg, lang).then(r => { if (id === reqId.current) { setRes(r.data); setSort(null); } })
      .catch(e => toast.error(apiError(e, t))).finally(() => { if (id === reqId.current) setBusy(false); }), 250);
    return () => clearTimeout(h);
  }, [cfg, lang]); // eslint-disable-line

  const ds = useMemo(() => meta?.datasets.find(d => d.key === cfg?.dataset), [meta, cfg]);
  const allDims = useMemo(() => (ds ? [...ds.time.map(x => ({ ...x, time: true })), ...ds.dims] : []), [ds]);
  const dimLabel = k => allDims.find(d => d.key === k)?.label || k;

  if (!meta || !cfg || !ds) return <div className="loading-center"><div className="spinner" /></div>;

  const set = patch => setCfg(c => ({ ...c, ...patch }));
  const pickDataset = key => {
    const d = meta.datasets.find(x => x.key === key);
    setCfg(c => ({ dataset: key, range: c.range, date_from: c.date_from, date_to: c.date_to, rows: d.default.rows, column: null, measures: d.default.measures, filters: {} }));
    setTitle('');
  };
  const addRow = k => { if ((cfg.rows || []).includes(k) || (cfg.rows || []).length >= 3) return; set({ rows: [...(cfg.rows || []), k], column: cfg.column === k ? null : cfg.column }); };
  const rmRow = k => set({ rows: (cfg.rows || []).filter(x => x !== k) });
  const toggleMeasure = k => {
    const ms = cfg.measures || [];
    const next = ms.includes(k) ? ms.filter(x => x !== k) : [...ms, k];
    if (next.length) set({ measures: ds.measures.map(m => m.key).filter(x => next.includes(x)) });
  };
  const filters = cfg.filters || {};
  const setFilter = (dim, vals) => { const f = { ...filters }; if (vals.length) f[dim] = vals; else delete f[dim]; set({ filters: f }); };
  const applyPreset = p => { setCfg({ ...p.config }); setTitle(p.name); };
  const exportX = async () => {
    try { const r = await reportAPI.builderExport(cfg, lang, title || null); downloadBlob(r.data, `${(title || res?.dataset_label || 'report').replace(/[\\/:*?"<>|]+/g, '_')}_${today()}.xlsx`); }
    catch (e) { toast.error(t('error')); }
  };
  const savePreset = async () => {
    if (!saveBox.name.trim()) return toast.error(t('fill_required'));
    try { await reportAPI.builderSave({ name: saveBox.name.trim(), config: cfg, shared: saveBox.shared }); toast.success(t('saved')); setTitle(saveBox.name.trim()); setSaveBox(null); loadMeta(); }
    catch (e) { toast.error(apiError(e, t)); }
  };
  const delPreset = async p => { if (!window.confirm(t('confirm_delete'))) return; try { await reportAPI.builderDelete(p.id); loadMeta(); } catch (e) { toast.error(apiError(e, t)); } };

  // rows sorted on the client when a header is clicked
  const rows = (() => {
    if (!res) return [];
    const r = [...res.rows];
    if (!sort) return r;
    const val = x => (sort.key.startsWith('dim:') ? x.keys[+sort.key.slice(4)]?.label : sort.col ? (x.cells?.[sort.col]?.[sort.key] ?? 0) : x.values[sort.key] ?? 0);
    r.sort((a, b) => { const va = val(a), vb = val(b); const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), lang); return sort.dir * c; });
    return r;
  })();
  const sortBy = (key, col) => setSort(s => (s && s.key === key && s.col === col ? { key, col, dir: -s.dir } : { key, col, dir: key.startsWith('dim:') ? 1 : -1 }));
  const m0 = res?.measures[0];
  const chartData = res && m0 && res.rows.length > 0 && res.rows.length <= 40 ? rows.map(r => {
    const o = { name: r.keys.map(k => '\u2068' + k.label + '\u2069').join(' · ') || t('total') };
    if (res.column) res.col_values.forEach(cv => { o[String(cv.label)] = r.cells?.[cv.key]?.[m0.key] || 0; });
    else o[m0.label] = r.values[m0.key] || 0;
    return o;
  }) : null;
  const usedDims = new Set([...(cfg.rows || []), cfg.column].filter(Boolean));
  const vertical = chartData ? (chartData.length > 6 || Math.max(...chartData.map(c => c.name.length)) > 16) : false;
  const mixed = k => (res?.mixed_units || []).includes(k);   // kilos + metres + rolls: no meaningful total
  const short = v => (String(v).length > 26 ? String(v).slice(0, 25) + '…' : v);

  return (
    <div className="space-y-5">
      <SectionHero title={t('report_builder')} subtitle={res ? <>{res.dataset_label} · {res.record_count} {t('rb_records')}</> : null}>
        <button className="btn btn-secondary" onClick={() => setSaveBox({ name: title, shared: true })}><Save size={15} /> {t('rb_save')}</button>
        <button className="btn btn-primary" onClick={exportX} disabled={!res}><Download size={15} /> {t('rb_excel')}</button>
      </SectionHero>

      <div className="rb-presets">
        <span className="rb-presets-label"><Sparkles size={14} /> {t('rb_ready')}</span>
        {meta.builtin.map(p => <button key={p.id} className={`rb-chip ${title === p.name ? 'on' : ''}`} style={{ '--c': DS_COLOR[p.config.dataset] }} onClick={() => applyPreset(p)}>{p.name}</button>)}
        {meta.presets.length > 0 && <span className="rb-presets-label"><Bookmark size={14} /> {t('rb_saved')}</span>}
        {meta.presets.map(p => (
          <span key={p.id} className={`rb-chip mine ${title === p.name ? 'on' : ''}`} style={{ '--c': DS_COLOR[p.config.dataset] }}>
            <button onClick={() => applyPreset(p)}>{p.name}</button>
            {p.mine && <button className="x" title={t('delete')} onClick={() => delPreset(p)}><X size={12} /></button>}
          </span>
        ))}
      </div>

      <div className="rb-layout">
        <aside className="rb-panel card">
          <div className="rb-step"><span className="rb-step-title"><BarChart3 size={14} /> {t('rb_what')}</span>
            <div className="rb-ds">{meta.datasets.map(d => {
              const Icon = DS_ICON[d.key] || BarChart3;
              return <button key={d.key} className={cfg.dataset === d.key ? 'on' : ''} style={{ '--c': DS_COLOR[d.key] }} onClick={() => pickDataset(d.key)}><Icon size={16} /><span>{d.label}</span></button>;
            })}</div>
          </div>

          <div className="rb-step"><span className="rb-step-title"><CalendarRange size={14} /> {t('rb_when')}</span>
            <div className="mg-cats">{RANGES.map(r => <button key={r} className={cfg.range === r ? 'on' : ''} onClick={() => set({ range: r, ...(r === 'custom' && !cfg.date_from ? { date_from: today().slice(0, 8) + '01', date_to: today() } : {}) })}>{t('rb_range_' + r)}</button>)}</div>
            {cfg.range === 'custom' && <div className="flex gap-2 mt-2">
              <input className="input" type="date" value={cfg.date_from || ''} onChange={e => set({ date_from: e.target.value })} />
              <input className="input" type="date" value={cfg.date_to || ''} onChange={e => set({ date_to: e.target.value })} />
            </div>}
          </div>

          <div className="rb-step"><span className="rb-step-title"><Rows3 size={14} /> {t('rb_rows')}</span>
            <div className="rb-selected">
              {(cfg.rows || []).map((k, i) => <span key={k} className="rb-tag">{i + 1}. {dimLabel(k)}<button onClick={() => rmRow(k)}><X size={11} /></button></span>)}
              {(cfg.rows || []).length === 0 && <span className="rb-none">{t('rb_rows_none')}</span>}
            </div>
            {(cfg.rows || []).length < 3 && <select className="select" value="" onChange={e => e.target.value && addRow(e.target.value)}>
              <option value="">+ {t('rb_add_row')}</option>
              <optgroup label={t('rb_time')}>{ds.time.filter(d => !usedDims.has(d.key)).map(d => <option key={d.key} value={d.key}>{d.label}</option>)}</optgroup>
              <optgroup label={t('rb_fields')}>{ds.dims.filter(d => !usedDims.has(d.key)).map(d => <option key={d.key} value={d.key}>{d.label}</option>)}</optgroup>
            </select>}
          </div>

          <div className="rb-step"><span className="rb-step-title"><Columns3 size={14} /> {t('rb_columns')}</span>
            <select className="select" value={cfg.column || ''} onChange={e => set({ column: e.target.value || null })}>
              <option value="">{t('rb_no_columns')}</option>
              <optgroup label={t('rb_time')}>{ds.time.filter(d => !(cfg.rows || []).includes(d.key)).map(d => <option key={d.key} value={d.key}>{d.label}</option>)}</optgroup>
              <optgroup label={t('rb_fields')}>{ds.dims.filter(d => !(cfg.rows || []).includes(d.key)).map(d => <option key={d.key} value={d.key}>{d.label}</option>)}</optgroup>
            </select>
          </div>

          <div className="rb-step"><span className="rb-step-title"><Sigma size={14} /> {t('rb_values')}</span>
            <div className="rb-measures">{ds.measures.map(m => (
              <label key={m.key} className={(cfg.measures || []).includes(m.key) ? 'on' : ''}><input type="checkbox" checked={(cfg.measures || []).includes(m.key)} onChange={() => toggleMeasure(m.key)} /> {m.label}</label>
            ))}</div>
          </div>

          <div className="rb-step"><span className="rb-step-title"><Filter size={14} /> {t('rb_filters')}</span>
            {Object.entries(filters).map(([d, vals]) => {
              const facet = res?.facets?.[d] || [];
              const names = vals.map(v => facet.find(f => f.key === v)?.label ?? v);
              return <div key={d} className="rb-filter"><b>{dimLabel(d)}:</b> <span>{names.join('، ')}</span>
                <button onClick={() => setFilterPick({ dim: d, vals: [...vals] })}>{t('edit')}</button><button onClick={() => setFilter(d, [])}><X size={12} /></button></div>;
            })}
            <select className="select" value="" onChange={e => e.target.value && setFilterPick({ dim: e.target.value, vals: filters[e.target.value] || [] })}>
              <option value="">+ {t('rb_add_filter')}</option>
              {ds.dims.filter(d => res?.facets?.[d.key]?.length).map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
        </aside>

        <section className="rb-result">
          {!res ? <div className="card"><div className="loading-center"><div className="spinner" /></div></div> : <>
            <div className="rb-period"><CalendarRange size={13} /> <bdi>{String(res.date_from)} → {String(res.date_to)}</bdi>{title ? <b>{title}</b> : null}</div>
            <div className={`rb-totals ${busy ? 'busy' : ''}`}>
              {res.measures.map((m, i) => <div key={m.key} className="rb-total" style={{ '--c': PALETTE[i % PALETTE.length] }}><span>{m.label}</span>
                {mixed(m.key) ? <><b>—</b><small className="rb-mixed">{t('rb_mixed_units')}</small></> : <b><bdi>{numFmt(m.key, res.totals[m.key])}</bdi></b>}</div>)}
            </div>

            {chartData && chartData.length > 1 && (
              <div className="card rb-chart">
                <div className="rb-chart-title">{m0.label}{res.column ? ` · ${res.column.label}` : ''}</div>
                <ResponsiveContainer width="100%" height={vertical ? Math.min(560, Math.max(220, chartData.length * 30 + 70)) : 280}>
                  <BarChart data={chartData} layout={vertical ? 'vertical' : 'horizontal'} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    {vertical
                      ? <><XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} /><YAxis type="category" dataKey="name" width={180} interval={0} tick={({ x, y, payload }) => (
                          // own tick: in an RTL page "end" would mean the left edge, so the label ran over the bars and "(kg)" broke apart
                          <text x={x} y={y} dy={4} textAnchor="end" direction="ltr" fill="#cbd5e1" fontSize={11}>{short(payload.value)}</text>)} /></>
                      : <><XAxis dataKey="name" tick={{ fill: '#cbd5e1', fontSize: 11 }} interval={0} /><YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} /></>}
                    <Tooltip contentStyle={{ background: '#0c1020', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 12 }} formatter={v => fmt(v, 2)} />
                    {res.column ? <>{res.col_values.slice(0, 12).map((cv, i) => <Bar key={cv.key} dataKey={String(cv.label)} stackId="a" fill={PALETTE[i % PALETTE.length]} />)}<Legend wrapperStyle={{ fontSize: 11 }} /></>
                      : <Bar dataKey={m0.label} fill={DS_COLOR[res.dataset]} radius={[4, 4, 4, 4]} />}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {res.rows.length === 0 ? <div className="empty-state"><BarChart3 size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('rb_empty')}</p></div> : (
                <div className="table-wrap rb-table-wrap"><table className="rb-table">
                  <thead>
                    {res.column && res.measures.length > 1 && <tr>
                      {(res.rows_dims.length ? res.rows_dims : [{ key: '_', label: '' }]).map(d => <th key={d.key} />)}
                      {res.col_values.map(cv => <th key={cv.key} colSpan={res.measures.length} className="rb-colgroup">{cv.label}</th>)}
                      <th colSpan={res.measures.length} className="rb-colgroup tot">{t('total')}</th>
                    </tr>}
                    <tr>
                      {(res.rows_dims.length ? res.rows_dims : [{ key: '_', label: '' }]).map((d, i) => <th key={d.key} className="sortable" onClick={() => sortBy('dim:' + i)}>{d.label}</th>)}
                      {res.column && res.col_values.map(cv => res.measures.map(m => (
                        <th key={cv.key + m.key} className="num sortable" onClick={() => sortBy(m.key, cv.key)}>{res.measures.length > 1 ? m.label : cv.label}{sort && sort.key === m.key && sort.col === cv.key ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}</th>
                      )))}
                      {res.measures.map(m => <th key={m.key} className="num sortable tot" onClick={() => sortBy(m.key)}>{res.column ? (res.measures.length > 1 ? m.label : t('total')) : m.label}{sort && sort.key === m.key && !sort.col ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}</th>)}
                    </tr>
                  </thead>
                  <tbody>{rows.map((r, ri) => (
                    <tr key={ri}>
                      {(r.keys.length ? r.keys : [{ key: '_', label: t('total') }]).map((k, i) => <td key={i} className={i === 0 ? 'rb-key' : 'rb-key2'}>{k.label}</td>)}
                      {res.column && res.col_values.map(cv => res.measures.map(m => <td key={cv.key + m.key} className="num"><bdi>{numFmt(m.key, r.cells?.[cv.key]?.[m.key])}</bdi></td>))}
                      {res.measures.map(m => <td key={m.key} className="num tot"><bdi>{numFmt(m.key, r.values[m.key])}</bdi></td>)}
                    </tr>
                  ))}</tbody>
                  {res.rows_dims.length > 0 && <tfoot><tr>
                    <td colSpan={res.rows_dims.length}>{t('total')}</td>
                    {res.column && res.col_values.map(cv => res.measures.map(m => <td key={cv.key + m.key} className="num">{mixed(m.key) ? '—' : <bdi>{numFmt(m.key, res.col_totals[cv.key]?.[m.key])}</bdi>}</td>))}
                    {res.measures.map(m => <td key={m.key} className="num">{mixed(m.key) ? '—' : <bdi>{numFmt(m.key, res.totals[m.key])}</bdi>}</td>)}
                  </tr></tfoot>}
                </table></div>
              )}
            </div>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}><ArrowDownWideNarrow size={12} style={{ verticalAlign: -2 }} /> {t('rb_hint')}</p>
          </>}
        </section>
      </div>

      {filterPick && (() => {
        const facet = res?.facets?.[filterPick.dim] || [];
        const on = v => filterPick.vals.includes(v);
        return (
          <Modal title={`${t('rb_filters')}: ${dimLabel(filterPick.dim)}`} onClose={() => setFilterPick(null)} style={{ maxWidth: 460 }}
            footer={<><button className="btn btn-secondary" onClick={() => setFilterPick(null)}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={() => { setFilter(filterPick.dim, filterPick.vals); setFilterPick(null); }}>{t('rb_apply')}</button></>}>
            <div className="flex gap-2" style={{ marginBottom: 10 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setFilterPick(f => ({ ...f, vals: facet.map(x => x.key) }))}>{t('rb_select_all')}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setFilterPick(f => ({ ...f, vals: [] }))}>{t('rb_select_none')}</button>
            </div>
            <div className="rb-facets">{facet.map(x => (
              <label key={x.key} className={on(x.key) ? 'on' : ''}><input type="checkbox" checked={on(x.key)}
                onChange={() => setFilterPick(f => ({ ...f, vals: on(x.key) ? f.vals.filter(v => v !== x.key) : [...f.vals, x.key] }))} /> {x.label}</label>
            ))}</div>
          </Modal>
        );
      })()}

      {saveBox && (
        <Modal title={t('rb_save')} onClose={() => setSaveBox(null)} style={{ maxWidth: 420 }}
          footer={<><button className="btn btn-secondary" onClick={() => setSaveBox(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={savePreset}><Plus size={14} /> {t('save')}</button></>}>
          <div className="form-group"><label className="form-label">{t('rb_report_name')}</label><input className="input" autoFocus value={saveBox.name} onChange={e => setSaveBox(s => ({ ...s, name: e.target.value }))} /></div>
          <label className="ex-check"><input type="checkbox" checked={saveBox.shared} onChange={e => setSaveBox(s => ({ ...s, shared: e.target.checked }))} /> {t('rb_shared')}</label>
          <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>{t('rb_save_hint')}</p>
        </Modal>
      )}
    </div>
  );
}
